<template>
  <footer class="footer">
    <div class="footer-main">
      <div class="footer-container">
        <div class="footer-grid">
          <div class="footer-column footer-brand">
            <div class="footer-logo">
              <img v-if="company?.logo" :src="company.logo" class="footer-logo-img" alt="MegaMaker">
              <template v-else>
                <svg width="40" height="40" viewBox="0 0 40 40" fill="none">
                  <rect x="5" y="10" width="30" height="20" stroke="white" stroke-width="2.5" fill="none"/>
                  <line x1="20" y1="10" x2="20" y2="5" stroke="#2196F3" stroke-width="2.5"/>
                  <circle cx="20" cy="25" r="4" fill="#2196F3"/>
                  <rect x="10" y="30" width="20" height="2.5" fill="white"/>
                </svg>
                <span>MegaMaker</span>
              </template>
            </div>
            <p class="footer-desc">{{ $t('footer.desc') }}</p>
            <div class="footer-social">
              <div v-for="s in socials" :key="s.key" class="social-item">
                <div class="social-icon" :style="{ background: s.color }">
                  <svg viewBox="0 0 24 24" width="22" height="22" fill="#FFFFFF" aria-hidden="true">
                    <path :d="s.path" />
                  </svg>
                </div>
                <div class="social-qr">
                  <span class="qr-name">{{ $t(s.nameKey) }}</span>
                  <img v-if="s.qr" :src="s.qr" class="qr-img" :alt="$t(s.nameKey)" />
                  <span v-else class="qr-empty">{{ $t('footer.qrPending') }}</span>
                </div>
              </div>
            </div>
          </div>

          <div class="footer-column">
            <h3 class="footer-title">{{ $t('footer.products') }}</h3>
            <ul class="footer-links">
              <li><a href="/equipment/single-wire">{{ $t('nav.equipmentSingle') }}</a></li>
              <li><a href="/equipment/multi-beam">{{ $t('nav.equipmentMulti') }}</a></li>
              <li><a href="/equipment/titanium">{{ $t('nav.equipmentTitanium') }}</a></li>
              <li><a href="/services/lattice">{{ $t('nav.lattice') }}</a></li>
              <li><a href="/services/concrete">{{ $t('nav.concrete') }}</a></li>
            </ul>
          </div>

          <div class="footer-column">
            <h3 class="footer-title">{{ $t('footer.solutions') }}</h3>
            <ul class="footer-links">
              <li><a href="/solutions/ship">{{ $t('nav.ship') }}</a></li>
              <li><a href="/solutions/platform">{{ $t('nav.platform') }}</a></li>
              <li><a href="/solutions/deep-sea">{{ $t('nav.deepSea') }}</a></li>
            </ul>
          </div>

          <div class="footer-column">
            <h3 class="footer-title">{{ $t('footer.about') }}</h3>
            <ul class="footer-links">
              <li><a href="/about">{{ $t('footer.intro') }}</a></li>
              <li><a href="/news">{{ $t('footer.news') }}</a></li>
              <li><a href="#" @click.prevent="openQuote">{{ $t('footer.contact') }}</a></li>
            </ul>
          </div>

          <div class="footer-column">
            <h3 class="footer-title">{{ $t('footer.contactTitle') }}</h3>
            <ul class="footer-contact">
              <li>{{ $t('footer.address') }}</li>
              <li>{{ $t('footer.phone') }}</li>
              <li>{{ $t('footer.email') }}</li>
              <li>{{ $t('footer.hours') }}</li>
            </ul>
          </div>
        </div>
      </div>
    </div>

    <div class="footer-bottom">
      <div class="footer-container">
        <div class="footer-bottom-content">
          <div class="footer-copyright-block">
            <p class="copyright">{{ $t('footer.copyright') }}</p>
            <a
              class="icp-link"
              href="https://beian.miit.gov.cn/"
              target="_blank"
              rel="noopener noreferrer"
            >京ICP备2026066746号</a>
          </div>
          <div class="footer-bottom-links">
            <a href="#" @click.prevent="openPolicy('privacy')">{{ $t('footer.privacy') }}</a>
            <span class="divider">|</span>
            <a href="#" @click.prevent="openPolicy('terms')">{{ $t('footer.terms') }}</a>
            <span class="divider">|</span>
            <a href="#" @click.prevent="openSiteMap">{{ $t('footer.sitemap') }}</a>
          </div>
        </div>
      </div>
    </div>

    <!-- 隐私政策 / 使用条款弹窗 -->
    <transition name="policy-fade">
      <div v-if="policyDialog" class="policy-mask" @click.self="closePolicy">
        <div class="policy-panel" role="dialog" aria-modal="true">
          <div class="policy-head">
            <h4>{{ policyTitle }}</h4>
            <button class="policy-close" @click="closePolicy" aria-label="close">×</button>
          </div>
          <div class="policy-body">
            <p v-for="(para, i) in policyParagraphs" :key="i" class="policy-para">{{ para }}</p>
          </div>
          <div class="policy-foot">
            <button class="policy-ok" @click="closePolicy">{{ $t('footer.gotIt') }}</button>
          </div>
        </div>
      </div>
    </transition>
  </footer>
</template>

<script>
import { ref, computed, onMounted, onUnmounted, watch } from 'vue'
import { useRoute } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { legalPolicyApi, companyApi, systemConfigApi } from '../api'
import { openInquiry } from '../utils/inquiry'

export default {
  name: 'Footer',
  setup() {
    const route = useRoute()
    const { tm, t, locale } = useI18n()
    const company = ref(null)

    // 页尾"网站地图"跳转链接(后台 系统配置 → map_url,后台修改后进入页面/切换路由即刷新)
    const mapUrl = ref('')
    const loadSiteMap = async () => {
      try {
        const data = await systemConfigApi()
        mapUrl.value = data?.map_url || ''
      } catch (e) {
        mapUrl.value = ''
      }
    }

    // 每次进入页面 / 切换路由都拉取最新公司信息（二维码后台上传后即时生效）
    const loadCompany = async () => {
      try {
        company.value = await companyApi()
      } catch (e) {
        // 失败静默，二维码区域显示"待上传"
      }
    }

    const socials = computed(() => [
      {
        key: 'wechat',
        nameKey: 'footer.wechat',
        color: '#07C160',
        qr: company.value?.wechatQr || '',
        path: "M8.691 2.188C3.891 2.188 0 5.476 0 9.53c0 2.212 1.17 4.203 3.002 5.55a.59.59 0 0 1 .213.665l-.39 1.48c-.019.07-.048.141-.048.213 0 .163.13.295.29.295a.326.326 0 0 0 .167-.054l1.903-1.114a.864.864 0 0 1 .717-.098 10.16 10.16 0 0 0 2.837.403c.276 0 .543-.027.811-.05-.857-2.578.157-4.972 1.932-6.446 1.703-1.415 3.882-1.98 5.853-1.838-.576-3.583-4.196-6.348-8.596-6.348zM5.785 5.991c.642 0 1.162.529 1.162 1.18a1.17 1.17 0 0 1-1.162 1.178A1.17 1.17 0 0 1 4.623 7.17c0-.651.52-1.18 1.162-1.18zm5.813 0c.642 0 1.162.529 1.162 1.18a1.17 1.17 0 0 1-1.162 1.178 1.17 1.17 0 0 1-1.162-1.178c0-.651.52-1.18 1.162-1.18zm5.34 2.867c-1.797-.052-3.746.512-5.28 1.786-1.72 1.428-2.687 3.72-1.78 6.22.942 2.453 3.666 4.229 6.884 4.229.826 0 1.622-.12 2.361-.336a.722.722 0 0 1 .598.082l1.584.926a.272.272 0 0 0 .14.047c.134 0 .24-.111.24-.247 0-.06-.023-.12-.038-.177l-.327-1.233a.582.582 0 0 1-.023-.156.49.49 0 0 1 .201-.398C23.024 18.48 24 16.82 24 14.98c0-3.21-2.931-5.837-6.656-6.088V8.89c-.135-.01-.27-.027-.407-.03zm-2.53 3.274c.535 0 .969.44.969.982a.976.976 0 0 1-.969.983.976.976 0 0 1-.969-.983c0-.542.434-.982.97-.982zm4.844 0c.535 0 .969.44.969.982a.976.976 0 0 1-.969.983.976.976 0 0 1-.969-.983c0-.542.434-.982.969-.982z"
      },
      {
        key: 'weibo',
        nameKey: 'footer.weibo',
        color: '#E6162D',
        qr: company.value?.weiboQr || '',
        path: "M10.098 20.323c-3.977.391-7.414-1.406-7.672-4.02-.259-2.609 2.759-5.047 6.74-5.441 3.979-.394 7.413 1.404 7.671 4.018.259 2.6-2.759 5.049-6.737 5.439l-.002.004zM9.05 17.219c-.384.616-1.208.884-1.829.602-.612-.279-.793-.991-.406-1.593.379-.595 1.176-.861 1.793-.601.622.263.82.972.442 1.592zm1.27-1.627c-.141.237-.449.353-.689.253-.236-.09-.313-.361-.177-.586.138-.227.436-.346.672-.24.239.09.315.36.18.601l.014-.028zm.176-2.719c-1.893-.493-4.033.45-4.857 2.118-.836 1.704-.026 3.591 1.886 4.21 1.983.64 4.318-.341 5.132-2.179.8-1.793-.201-3.642-2.161-4.149zm7.563-1.224c-.346-.105-.57-.18-.405-.615.375-.977.42-1.804 0-2.404-.781-1.112-2.915-1.053-5.364-.03 0 0-.766.331-.571-.271.376-1.217.315-2.224-.27-2.809-1.338-1.337-4.869.045-7.888 3.08C1.309 10.87 0 13.273 0 15.348c0 3.981 5.099 6.395 10.086 6.395 6.536 0 10.888-3.801 10.888-6.82 0-1.822-1.547-2.854-2.915-3.284v.01zm1.908-5.092c-.766-.856-1.908-1.187-2.96-.962-.436.09-.706.511-.616.932.09.42.511.691.932.602.511-.105 1.067.044 1.442.465.376.421.466.977.316 1.473-.136.406.089.856.51.992.405.119.857-.105.992-.512.33-1.021.12-2.178-.646-3.035l.03.045zm2.418-2.195c-1.576-1.757-3.905-2.419-6.054-1.968-.496.104-.812.587-.706 1.081.104.496.586.813 1.082.707 1.532-.331 3.185.15 4.296 1.383 1.112 1.246 1.429 2.943.947 4.416-.165.48.106 1.007.586 1.157.479.165.991-.104 1.157-.586.675-2.088.241-4.478-1.338-6.235l.03.045z"
      },
      {
        key: 'xiaohongshu',
        nameKey: 'footer.xiaohongshu',
        color: '#FF2442',
        qr: company.value?.xhsQr || '',
        path: "M22.405 9.879c.002.016.01.02.07.019h.725a.797.797 0 0 0 .78-.972.794.794 0 0 0-.884-.618.795.795 0 0 0-.692.794c0 .101-.002.666.001.777zm-11.509 4.808c-.203.001-1.353.004-1.685.003a2.528 2.528 0 0 1-.766-.126.025.025 0 0 0-.03.014L7.7 16.127a.025.025 0 0 0 .01.032c.111.06.336.124.495.124.66.01 1.32.002 1.981 0 .01 0 .02-.006.023-.015l.712-1.545a.025.025 0 0 0-.024-.036zM.477 9.91c-.071 0-.076.002-.076.01a.834.834 0 0 0-.01.08c-.027.397-.038.495-.234 3.06-.012.24-.034.389-.135.607-.026.057-.033.042.003.112.046.092.681 1.523.787 1.74.008.015.011.02.017.02.008 0 .033-.026.047-.044.147-.187.268-.391.371-.606.306-.635.44-1.325.486-1.706.014-.11.021-.22.03-.33l.204-2.616.022-.293c.003-.029 0-.033-.03-.034zm7.203 3.757a1.427 1.427 0 0 1-.135-.607c-.004-.084-.031-.39-.235-3.06a.443.443 0 0 0-.01-.082c-.004-.011-.052-.008-.076-.008h-1.48c-.03.001-.034.005-.03.034l.021.293c.076.982.153 1.964.233 2.946.05.4.186 1.085.487 1.706.103.215.223.419.37.606.015.018.037.051.048.049.02-.003.742-1.642.804-1.765.036-.07.03-.055.003-.112zm3.861-.913h-.872a.126.126 0 0 1-.116-.178l1.178-2.625a.025.025 0 0 0-.023-.035l-1.318-.003a.148.148 0 0 1-.135-.21l.876-1.954a.025.025 0 0 0-.023-.035h-1.56c-.01 0-.02.006-.024.015l-.926 2.068c-.085.169-.314.634-.399.938a.534.534 0 0 0-.02.191.46.46 0 0 0 .23.378.981.981 0 0 0 .46.119h.59c.041 0-.688 1.482-.834 1.972a.53.53 0 0 0-.023.172.465.465 0 0 0 .23.398c.15.092.342.12.475.12l1.66-.001c.01 0 .02-.006.023-.015l.575-1.28a.025.025 0 0 0-.024-.035zm-6.93-4.937H3.1a.032.032 0 0 0-.034.033c0 1.048-.01 2.795-.01 6.829 0 .288-.269.262-.28.262h-.74c-.04.001-.044.004-.04.047.001.037.465 1.064.555 1.263.01.02.03.033.051.033.157.003.767.009.938-.014.153-.02.3-.06.438-.132.3-.156.49-.419.595-.765.052-.172.075-.353.075-.533.002-2.33 0-4.66-.007-6.991a.032.032 0 0 0-.032-.032zm11.784 6.896c0-.014-.01-.021-.024-.022h-1.465c-.048-.001-.049-.002-.05-.049v-4.66c0-.072-.005-.07.07-.07h.863c.08 0 .075.004.075-.074V8.393c0-.082.006-.076-.08-.076h-3.5c-.064 0-.075-.006-.075.073v1.445c0 .083-.006.077.08.077h.854c.075 0 .07-.004.07.07v4.624c0 .095.008.084-.085.084-.37 0-1.11-.002-1.304 0-.048.001-.06.03-.06.03l-.697 1.519s-.014.025-.008.036c.006.01.013.008.058.008 1.748.003 3.495.002 5.243.002.03-.001.034-.006.035-.033v-1.539zm4.177-3.43c0 .013-.007.023-.02.024-.346.006-.692.004-1.037.004-.014-.002-.022-.01-.022-.024-.005-.434-.007-.869-.01-1.303 0-.072-.006-.071.07-.07l.733-.003c.041 0 .081.002.12.015.093.025.16.107.165.204.006.431.002 1.153.001 1.153zm2.67.244a1.953 1.953 0 0 0-.883-.222h-.18c-.04-.001-.04-.003-.042-.04V10.21c0-.132-.007-.263-.025-.394a1.823 1.823 0 0 0-.153-.53 1.533 1.533 0 0 0-.677-.71 2.167 2.167 0 0 0-1-.258c-.153-.003-.567 0-.72 0-.07 0-.068.004-.068-.065V7.76c0-.031-.01-.041-.046-.039H17.93s-.016 0-.023.007c-.006.006-.008.012-.008.023v.546c-.008.036-.057.015-.082.022h-.95c-.022.002-.028.008-.03.032v1.481c0 .09-.004.082.082.082h.913c.082 0 .072.128.072.128V11.19s.003.117-.06.117h-1.482c-.068 0-.06.082-.06.082v1.445s-.01.068.064.068h1.457c.082 0 .076-.006.076.079v3.225c0 .088-.007.081.082.081h1.43c.09 0 .082.007.082-.08v-3.27c0-.029.006-.035.033-.035l2.323-.003c.098 0 .191.02.28.061a.46.46 0 0 1 .274.407c.008.395.003.79.003 1.185 0 .259-.107.367-.33.367h-1.218c-.023.002-.029.008-.028.033.184.437.374.871.57 1.303a.045.045 0 0 0 .04.026c.17.005.34.002.51.003.15-.002.517.004.666-.01a2.03 2.03 0 0 0 .408-.075c.59-.18.975-.698.976-1.313v-1.981c0-.128-.01-.254-.034-.38 0 .078-.029-.641-.724-.998z"
      }
    ])

    onMounted(() => {
      loadCompany()
      loadSiteMap()
    })
    watch(() => route.path, () => {
      loadCompany()
      loadSiteMap()
    })

    // 联系我们：打开全局报价/咨询弹窗
    const openQuote = () => openInquiry()

    // 网站地图：新标签页打开后台配置的百度地图链接(未配置时给出提示)
    const openSiteMap = () => {
      const url = mapUrl.value
      if (url) {
        window.open(url, '_blank', 'noopener')
      } else {
        window.alert(t('footer.mapPending'))
      }
    }

    // ===== 隐私政策 / 使用条款弹窗 =====
    const policyDialog = ref(null) // 'privacy' | 'terms' | null
    const remotePolicy = ref(null) // 后台配置的政策内容（legal_policy 表）

    const openPolicy = async (type) => {
      policyDialog.value = type
      remotePolicy.value = null
      try {
        const data = await legalPolicyApi(type)
        remotePolicy.value = data || null
      } catch (e) {
        // 拉取失败则回退内置文案
        remotePolicy.value = null
      }
    }
    const closePolicy = () => {
      policyDialog.value = null
    }
    const onKey = (e) => {
      if (e.key === 'Escape') closePolicy()
    }
    onMounted(() => document.addEventListener('keydown', onKey))
    onUnmounted(() => document.removeEventListener('keydown', onKey))
    watch(policyDialog, (v) => {
      document.body.style.overflow = v ? 'hidden' : ''
    })

    // 标题：后台配置优先，其次内置 i18n
    const policyTitle = computed(() => {
      const pt = policyDialog.value
      if (!pt) return ''
      const remote = remotePolicy.value
      if (remote) {
        const val = locale.value === 'en-US' ? (remote.titleEn || remote.title) : remote.title
        if (val) return val
      }
      return pt === 'privacy' ? t('footer.privacy') : t('footer.terms')
    })

    // 段落：后台配置正文（按空行分段）优先，其次内置 i18n
    const policyParagraphs = computed(() => {
      const t = policyDialog.value
      if (!t) return []
      const remote = remotePolicy.value
      if (remote) {
        const raw = locale.value === 'en-US' ? (remote.contentEn || remote.content) : remote.content
        if (raw) {
          return raw.split(/\n{2,}/).map((s) => s.trim()).filter(Boolean)
        }
      }
      const list = tm(`legal.${t}`)
      return Array.isArray(list) ? list : []
    })

    return { company, socials, openQuote, openSiteMap, policyDialog, policyTitle, policyParagraphs, openPolicy, closePolicy }
  }
}
</script>

<style scoped>
.footer {
  background: #0B1A2D;
  color: #FFFFFF;
}

.footer-main {
  padding: 80px 0 40px;
}

.footer-container {
  max-width: 1400px;
  margin: 0 auto;
  padding: 0 40px;
}

.footer-grid {
  display: grid;
  grid-template-columns: 2fr 1fr 1fr 1fr 1.5fr;
  gap: 60px;
}

.footer-brand {
  padding-right: 40px;
}

.footer-logo-img {
  height: 40px;
  width: auto;
  max-width: 200px;
  object-fit: contain;
}

.footer-logo {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 20px;
  font-size: 24px;
  font-weight: 700;
}

.footer-desc {
  color: rgba(255, 255, 255, 0.7);
  font-size: 14px;
  line-height: 1.8;
  margin-bottom: 24px;
}

.footer-social {
  display: flex;
  gap: 14px;
  margin-top: 24px;
}

.social-item {
  position: relative;
  cursor: pointer;
}

.social-icon {
  width: 44px;
  height: 44px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.25);
  transition: transform 0.3s ease, box-shadow 0.3s ease;
}

.social-item:hover .social-icon {
  transform: translateY(-5px);
  box-shadow: 0 6px 16px rgba(0, 0, 0, 0.35);
}

/* hover 二维码浮层 */
.social-qr {
  position: absolute;
  bottom: calc(100% + 14px);
  left: 50%;
  transform: translateX(-50%);
  background: #FFFFFF;
  border-radius: 10px;
  padding: 12px;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.28);
  display: none;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  z-index: 30;
  min-width: 148px;
}

.social-item:hover .social-qr {
  display: flex;
}

.social-qr::after {
  content: "";
  position: absolute;
  top: 100%;
  left: 50%;
  transform: translateX(-50%);
  border: 7px solid transparent;
  border-top-color: #FFFFFF;
}

.qr-name {
  font-size: 13px;
  font-weight: 600;
  color: #1A1A1A;
}

.qr-img {
  width: 130px;
  height: 130px;
  border-radius: 6px;
  display: block;
  object-fit: contain;
}

.qr-empty {
  font-size: 12px;
  color: #98A2B3;
  line-height: 1.5;
  text-align: center;
  padding: 20px 4px;
}

.footer-title {
  font-size: 16px;
  font-weight: 600;
  margin-bottom: 24px;
  color: #FFFFFF;
}

.footer-links {
  list-style: none;
  padding: 0;
  margin: 0;
}

.footer-links li {
  margin-bottom: 12px;
}

.footer-links a {
  color: rgba(255, 255, 255, 0.7);
  text-decoration: none;
  font-size: 14px;
  transition: all 0.3s ease;
  display: inline-block;
}

.footer-links a:hover {
  color: #2196F3;
  transform: translateX(4px);
}

.footer-contact {
  list-style: none;
  padding: 0;
  margin: 0;
}

.footer-contact li {
  color: rgba(255, 255, 255, 0.7);
  font-size: 14px;
  margin-bottom: 12px;
  line-height: 1.6;
}

.footer-bottom {
  border-top: 1px solid rgba(255, 255, 255, 0.1);
  padding: 24px 0;
}

.footer-bottom-content {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.copyright {
  color: rgba(255, 255, 255, 0.5);
  font-size: 14px;
  margin: 0;
}

.footer-copyright-block {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.icp-link {
  color: rgba(255, 255, 255, 0.5);
  font-size: 13px;
  text-decoration: none;
  transition: color 0.3s ease;
}

.icp-link:hover {
  color: #2196F3;
}

.footer-bottom-links {
  display: flex;
  align-items: center;
  gap: 16px;
}

.footer-bottom-links a {
  color: rgba(255, 255, 255, 0.5);
  text-decoration: none;
  font-size: 13px;
  transition: color 0.3s ease;
}

.footer-bottom-links a:hover {
  color: #2196F3;
}

.divider {
  color: rgba(255, 255, 255, 0.2);
}

/* ===== 隐私政策 / 使用条款弹窗 ===== */
.policy-mask {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.55);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
  z-index: 999;
}

.policy-panel {
  background: #FFFFFF;
  color: #1A1A1A;
  border-radius: 12px;
  width: 100%;
  max-width: 680px;
  max-height: 78vh;
  display: flex;
  flex-direction: column;
  box-shadow: 0 12px 40px rgba(0, 0, 0, 0.3);
}

.policy-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 18px 24px;
  border-bottom: 1px solid #E8ECF1;
}

.policy-head h4 {
  margin: 0;
  font-size: 18px;
  font-weight: 700;
  color: #0B4F8C;
}

.policy-close {
  border: none;
  background: transparent;
  font-size: 26px;
  line-height: 1;
  color: #98A2B3;
  cursor: pointer;
  padding: 0 4px;
}

.policy-close:hover {
  color: #1A1A1A;
}

.policy-body {
  padding: 20px 24px;
  overflow-y: auto;
  flex: 1;
}

.policy-para {
  margin: 0 0 14px;
  font-size: 14px;
  line-height: 1.9;
  color: #3A4657;
  text-align: justify;
}

.policy-para:last-child {
  margin-bottom: 0;
}

.policy-foot {
  padding: 14px 24px;
  border-top: 1px solid #E8ECF1;
  text-align: right;
}

.policy-ok {
  background: #0B4F8C;
  color: #FFFFFF;
  border: none;
  border-radius: 20px;
  padding: 8px 32px;
  font-size: 14px;
  cursor: pointer;
  transition: background 0.3s ease;
}

.policy-ok:hover {
  background: #2196F3;
}

.policy-fade-enter-active,
.policy-fade-leave-active {
  transition: opacity 0.25s ease;
}

.policy-fade-enter-from,
.policy-fade-leave-to {
  opacity: 0;
}

/* ===== 移动端 ===== */
@media (max-width: 900px) {
  .footer-grid {
    grid-template-columns: 1fr 1fr;
    gap: 32px;
  }
  .footer-brand {
    grid-column: 1 / -1;
  }
  /* 窄屏下二维码浮层以图标中心居中会向左/右溢出屏幕：
     微信(首项)靠左展开、小红书(末项)靠右展开，中间项保持居中 */
  .social-item:first-child .social-qr {
    left: 0;
    transform: none;
  }
  .social-item:last-child .social-qr {
    left: auto;
    right: 0;
    transform: none;
  }
  .social-item:first-child .social-qr::after {
    left: 22px;
    transform: none;
  }
  .social-item:last-child .social-qr::after {
    left: auto;
    right: 22px;
    transform: none;
  }
}

@media (max-width: 480px) {
  .footer-grid {
    grid-template-columns: 1fr;
  }
  .footer-bottom-content {
    flex-direction: column;
    gap: 12px;
    text-align: center;
  }
  .footer-container {
    padding: 0 16px;
  }
}
</style>